import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import moment from 'moment';
import styled from 'styled-components';
import PlayerHeader from '../components/PlayerHeader';
import MeasurementChart from '../components/MeasurementChart';
import MeasurementSessionsTable from '../components/MeasurementSessionsTable';
import Measurement, { configuration } from '../services/Measurement';
import DateToggle from '../components/PlayerChartCards/DateToggle';
import LoaderAnimation from '../components/LoaderAnimation';
import SessionToggle from '../components/PlayerChartCards/SessionToggle';
import Button from '../components/Button';

const HorizontalDivider = styled.hr`
  width: max(89%, 1284px);
  margin: 0 auto;
`;

const MeasurementHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  padding: 32px max(10%, 148px) 24px;
  align-items: center;
`;

const ToggleWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  padding-left: 1rem;
`;

const ChartTitle = styled.h2(({ theme }) => {
  const { color, type } = theme;

  return `
      font-size: ${type.size.larger};
      font-weight: ${type.weight.bold};
      color: ${color.grayscale[0]};
      margin-right: 58px;
    `;
});

const StyledDateToggle = styled(DateToggle)`
  flex-shrink: 1;
`;

const BlankBody = styled.div(({ theme }) => {
  const { color } = theme;
  return `
    display: flex;
    justify-content:center;
    align-items:center;
    min-height: 1000px;
    min-width: 100%;
    background: ${color.grayscale[900]}
`;
});

const ButtonWrapper = styled.div`
  width: max(89%, 1284px);
  margin: 0 auto;

  @media print {
    display: none;
  }
`;

const PlayerCardDetail = () => {
  const { dates } = configuration;
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  const initialSession = parseInt(params.get('session'));
  const initialRange = params.get('range');
  const [player, setPlayer] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [mySessionsParam, setMySessionsParam] = useState(isNaN(initialSession) ? 1 : initialSession);
  const [dateRangeParam, setDateRangeParam] = useState(initialRange !== null ? initialRange : dates.ALL.value);
  const history = useHistory();
  const { id, measurement } = useParams();

  useEffect(() => {
    const filterParams = {
      date_range: dateRangeParam,
      sessions: mySessionsParam,
    };

    Measurement.getMeasurementData(id, measurement, filterParams).then((response) => {
      const patient_info = {
        ...response.patient_info,
        dateCreated: moment(response.patient_info.dateCreated).format('LL'),
        dateLastActive: moment(response.patient_info.dateLastActive).format('LL'),
      };

      setPlayer(patient_info);
      setChartData(response.chart);
      setSessions(response.sessions);
    });
  }, [setPlayer, mySessionsParam, dateRangeParam, id, measurement]);

  const handleTabClick = (tab) => {
    const tabId = parseInt(tab);
    setMySessionsParam(tabId);

    history.replace({
      pathname,
      search: `?session=${tabId}&range=${dateRangeParam}`,
    });
  };

  const handleToggle = (range) => {
    setDateRangeParam(range);

    history.replace({
      pathname,
      search: `?session=${mySessionsParam}&range=${range}`,
    });
  };

  return (
    <div>
      {!player && (
        <BlankBody>
          <LoaderAnimation />
        </BlankBody>
      )}

      {player && (
        <>
          <PlayerHeader player={player} />
          <HorizontalDivider />
        </>
      )}

      <MeasurementHeader>
        {chartData && (
          <>
            <ChartTitle>{chartData.header.title.toUpperCase()}</ChartTitle>

            <StyledDateToggle selected={dateRangeParam} callback={handleToggle} />

            <ToggleWrapper>
              <SessionToggle
                options={[
                  {
                    label: 'MY SESSIONS',
                    value: 1,
                  },
                  {
                    label: 'ALL SESSIONS',
                    value: 0,
                  },
                ]}
                callback={handleTabClick}
                selected={mySessionsParam}
              />
            </ToggleWrapper>
          </>
        )}
      </MeasurementHeader>

      {chartData && <MeasurementChart data={chartData} />}
      {sessions && <MeasurementSessionsTable sessions={sessions} titleMeasurement={chartData.header.title} />}
      {chartData && (
        <ButtonWrapper>
          <Button type="button" onClick={window.print}>
            Print
          </Button>
        </ButtonWrapper>
      )}
    </div>
  );
};

const withState = connect((state) => {
  const { user, player } = state;

  return {
    user: user,
    specificPlayer: player.specific_player,
  };
});

export default withState(PlayerCardDetail);
